import { useCallback, useMemo } from "react";
import { FormikProps, getIn } from "formik";
import _ from "lodash";

import { AutoAwesomeOutlinedIcon } from "icons";
import theme from "theme";
import { colors } from "theme/palette";

import Autocomplete from "components/Autocomplete";
import Box from "components/Box";
import Button from "components/Button";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";

import { SectionValuesType } from "entities/MemoTemplate/components/SectionForm";
import {
  useMemoSectionAIPrompts,
  useMemoSourceDocuments,
} from "entities/MemoTemplate/sdk";

import MemoSectionAIPromptForm from "./MemoSectionAIPromptForm";

export interface IAIAnswerSection {
  formik: FormikProps<SectionValuesType>;
}
interface IMemoPromptOption {
  title: string;
  documents: Array<{
    id: number;
    name: string;
  }>;
  id: number;
  prompt: string;
}

const AIAnswerSection = ({ formik }: IAIAnswerSection) => {
  const { data: memoSectionAIPrompts } = useMemoSectionAIPrompts();
  const { data: sourceDocuments, isLoading: areSourceDocumentsLoading } =
    useMemoSourceDocuments();

  const memoPromptOptions = useMemo(
    () =>
      memoSectionAIPrompts?.map((prompt) => ({
        title: prompt.title,
        documents: [...prompt.source_documents],
        id: prompt.id,
        prompt: prompt.prompt,
      })) || [],
    [memoSectionAIPrompts]
  );

  const createNewMemoSectionAIPromptButtonClick = useCallback(() => {
    formik.setFieldValue("ai_prompt", {
      title: "",
      prompt: "",
      source_document_ids: [],
    });
  }, [formik]);

  const copyMemoSectionAIPromptButtonClick = useCallback(() => {
    formik.setFieldValue("ai_prompt", {
      ...formik.values.ai_prompt,
      id: undefined,
      title: `${formik.values.ai_prompt?.title} (copy)`,
    });
  }, [formik]);

  const memoSectionAIPromptHandleChange = useCallback(
    (event, option: IMemoPromptOption | null) => {
      if (_.isNil(option?.id)) {
        formik.setFieldValue("ai_prompt", null);
      } else {
        formik.setFieldValue("ai_prompt", {
          id: option.id,
          title: option.title,
          prompt: option.prompt,
          source_document_ids: option.documents.map((doc) => doc.id),
        });
      }
    },
    [formik]
  );

  return (
    <Box>
      <Text variant="text3" marginBottom={0.5}>
        Existing prompt
      </Text>
      <Autocomplete
        clearOnBlur
        selectOnFocus
        value={
          memoPromptOptions.find(
            (option) => option.id === formik.values.ai_prompt?.id
          ) || null
        }
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Select from the list..."
            helperText={
              getIn(formik.touched, "ai_prompt") &&
              !formik.values.ai_prompt &&
              getIn(formik.errors, "ai_prompt")
            }
            error={
              getIn(formik.touched, "ai_prompt") &&
              !formik.values.ai_prompt &&
              !_.isEmpty(getIn(formik.errors, "ai_prompt"))
            }
          />
        )}
        renderOption={(params, option) => (
          <Stack
            component="li"
            direction="row"
            gap={1}
            alignItems="center"
            flexWrap="wrap"
            sx={{
              padding: 1,
              "&:hover": {
                backgroundColor: colors.blue10,
              },
            }}
            {...params}
            key={params.id}
          >
            <Text fontWeight={500} variant="text2" noWrap>
              {option.title}
            </Text>
            {option.documents.map((document) => (
              <Stack
                height="24px"
                paddingRight={1}
                key={document.id}
                border={`1px solid ${colors.gray40}`}
                borderRadius={5}
                direction="row"
                alignItems="center"
                sx={{
                  backgroundColor: colors.gray5,
                }}
              >
                <AutoAwesomeOutlinedIcon
                  sx={{ color: colors.gray40, height: "15px" }}
                />
                <Text
                  variant="text4"
                  fontWeight={500}
                  width="fit-content"
                  noWrap
                >
                  {document.name}
                </Text>
              </Stack>
            ))}
          </Stack>
        )}
        options={memoPromptOptions}
        getOptionLabel={(option) => {
          return option.title;
        }}
        slotProps={{
          listbox: {
            sx: {
              border: `1px solid ${colors.gray40}`,
              padding: theme.spacing(2),
              boxShadow: `0 4px 8px 0 ${colors.gray20}`,
            },
          },
        }}
        onChange={memoSectionAIPromptHandleChange}
      />
      <Stack direction="row" gap={1} marginTop={1.5}>
        <Button
          variant="contained"
          color="secondary"
          onClick={createNewMemoSectionAIPromptButtonClick}
        >
          + Create a new prompt
        </Button>
        {!_.isNil(formik.values.ai_prompt?.id) && (
          <Button
            variant="contained"
            color="secondary"
            onClick={copyMemoSectionAIPromptButtonClick}
          >
            Copy & edit prompt
          </Button>
        )}
      </Stack>
      {!_.isNil(formik.values.ai_prompt) && (
        <MemoSectionAIPromptForm
          formik={formik}
          sourceDocuments={sourceDocuments}
          areSourceDocumentsLoading={areSourceDocumentsLoading}
          sourceDocumentIds={
            !_.isNil(formik.values.ai_prompt?.source_document_ids)
              ? formik.values.ai_prompt.source_document_ids
              : []
          }
        />
      )}
    </Box>
  );
};

export default AIAnswerSection;
